<template>
  <v-container>
    <landing-create :content="content"></landing-create>
    <loader :isLoading="isLoading"></loader>
    <view-component v-if="attributes" :attributes="attributes">
      <!-- it should be setup like this -->
      <!-- multiple project code alias (this is optional design) -->
      <template #multiple-codes-section>
        <v-list-item-title>
          <div v-if="projectCodeData?.additionalProjectCodes?.length">
            <div v-for="item in projectCodeData.additionalProjectCodes"
                :key="item.id"
                class="title-text"
                >
              {{ item.alias }}
            </div>
          </div>
          <div v-else>
            N/A
          </div>
        </v-list-item-title>

        <v-list-item-subtitle>
          <p class="subtitle-text">Project Code Alias</p>
        </v-list-item-subtitle>
      </template>
      <template #multiple-ethics-section>
        <div v-if="projectCodeData?.additionalEthicsNumbers?.length">
          <v-list-item-title>
            <v-row dense>
              <v-col
                v-for="item in projectCodeData.additionalEthicsNumbers"
                :key="item._id"
                cols="12"
                class="mb-2"
              >
                <v-card outlined dense class="pa-2">
                  <v-row class="added-ethics">
                    <v-col cols="12" sm="4">
                      <strong>{{ item.ethicsNumber }}</strong>
                      <p>Number</p>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <strong>{{ displayDate(item.ethicsDateApproved) }}</strong>
                      <p>Approved</p>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <strong>{{ displayDate(item.ethicsDateExpiration) }}</strong>
                      <p>Expiration:</p>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-list-item-title>
        </div>
        <div v-else>
          <v-list-item-title>N/A</v-list-item-title>
        </div>

        <v-list-item-subtitle>
          <p class="title-text">Additional Ethics</p>
        </v-list-item-subtitle>
      </template>
    </view-component>
  </v-container>
</template>

<!-- eslint-disable no-underscore-dangle -->
<script>
import Loader from '../../../components/utilities/Loader.vue';
import LandingCreate from '../../../components/layouts/LandingCreate.vue';
import ViewComponent from '../../../components/ViewComponent.vue';
import services from '../../../services';

const { medicalRegistry, documentRegistry, controlPanelRegistry } = services;
const {
  protocolNumberService,
  consentDocumentService,
  formService,
} = medicalRegistry;

const { serviceAgreementService, permitService } = documentRegistry;

const {
  userService,
  organizationService,
  departmentService,
} = controlPanelRegistry;

export default {
  name: 'ConsentDocumentView',
  components: {
    Loader,
    LandingCreate,
    ViewComponent,
  },
  data: () => ({
    isLoading: false,
    attributes: null,
    content: {
      pageTitle: 'Project Code View',
      enableBackButton: true,
    },
    departments: [],
    organizations: [],
    projectCode: {},
  }),
  async created() {
    this.isLoading = true;

    this.departments = await departmentService.getList();
    this.organizations = await organizationService.getList();

    const { id } = this.$route.params;
    const projectCodeData = await protocolNumberService.getOne({ id });
    const consentDocuments = await Promise.all(projectCodeData.consentDocuments.map(
      (cID) => consentDocumentService.getOne({ id: cID }),
    ));
    const permits = await Promise.all(projectCodeData.permits.map(
      (cID) => permitService.getOne({ id: cID }),
    ));
    const serviceAgreements = await Promise.all(projectCodeData.serviceAgreements.map(
      (cID) => serviceAgreementService.getOne({ id: cID }),
    ));
    this.projectCodeData = projectCodeData;
    console.log('this.projectCodeData', this.projectCodeData);
    this.attributes = await this.prepareAttribute({
      ...projectCodeData,
      consentDocuments,
      permits,
      serviceAgreements,
    });
    this.isLoading = false;
  },
  methods: {
    redirectToViewDocument(url) {
      window.open(url, '_blank');
    },
    getUsers(ids) {
      return ids.map((id) => userService.getOne({ id }));
    },
    getPrincipalInvUser(id) {
      return userService.getOne({ id });
    },
    mapUser(user) {
      const {
        _id: id,
        firstName,
        lastName,
      } = user;
      return { id, name: `${firstName}, ${lastName}` };
    },
    async prepareAttribute({
      _metadata, protocolNumber, dateApproved,
      consentDocuments, permits, serviceAgreements, otherDocuments, organizations, department,
      principalInvestigator, researchers, collaborators, medicalForms, ethicsNumber,
      dateEthicsApproved, dateExpiration,
    }) {
      const {
        created,
        updated,
      } = _metadata;
      const PIusers = await this.getPrincipalInvUser(principalInvestigator);
      const RSusers = await Promise.all(this.getUsers(researchers));
      const CRusers = await Promise.all(this.getUsers(collaborators));
      const forms = await Promise.all(medicalForms.map((id) => formService.getOne({ id })));
      return [
        {
          label: 'Project Code',
          value: protocolNumber,
        },
        // i want to add a custom here like
        // it should be same place/location when rendered
        {
          type: 'anchor',
          id: 'multiple-codes-section',
        },
        {
          label: 'Date Approved',
          value: dateApproved,
          type: 'date',
        },
        {
          label: 'Ethics Number',
          value: ethicsNumber,
        },
        {
          label: 'Ethics Date Approved',
          value: dateEthicsApproved,
          type: 'date',
        },
        {
          label: 'Ethics Date Expiration',
          value: dateExpiration,
          type: 'date',
        },
        {
          type: 'anchor',
          id: 'multiple-ethics-section',
        },
        {
          label: 'Consent Documents',
          value: consentDocuments.map((d) => ({ id: d._id, name: d.name })),
          reference: true,
          URLName: 'consent-document.view',
          type: 'array',
        },
        {
          label: 'Permits',
          value: permits.map((d) => ({ id: d._id, name: d.name })),
          reference: true,
          URLName: 'permit.view',
          type: 'array',
        },
        {
          label: 'Service Agreement Documents',
          value: serviceAgreements.map((d) => ({ id: d._id, name: d.name })),
          reference: true,
          URLName: 'service-agreement.view',
          type: 'array',
        },
        {
          label: 'Other Documents',
          value: otherDocuments?.url,
          display: otherDocuments?.url ? 'View Other Document' : '',
          type: 'file',
          onClick: () => {
            this.redirectToViewDocument(otherDocuments?.url);
          },
        },
        {
          label: 'Organization',
          value: organizations,
        },
        {
          label: 'Department',
          value: department,
        },
        {
          label: 'Principal Investigator',
          value: this.mapUser(PIusers),
          type: 'text',
          reference: true,
          URLName: 'user.view',
        },
        {
          label: 'Researcher',
          value: RSusers.map((u) => this.mapUser(u)),
          type: 'array',
          reference: true,
          URLName: 'user.view',
        },
        {
          label: 'Collaborator',
          value: CRusers.map((u) => this.mapUser(u)),
          type: 'array',
          reference: true,
          URLName: 'user.view',
        },
        {
          label: 'Medical Forms',
          value: forms.map((u) => ({ id: u._id, name: u.name })),
          type: 'array',
          reference: true,
          URLName: 'form.edit',
        },
        {
          label: 'Created by',
          value: { id: created.user._id, name: created.user.fullName },
          reference: true,
          URLName: 'user.view',
        },
        {
          label: 'Created date',
          value: created.date,
          type: 'datetime',
        },
        {
          label: 'Updated by',
          value: { id: updated.user._id, name: updated.user.fullName },
          reference: true,
          URLName: 'user.view',
        },
        {
          label: 'Updated dated',
          value: updated.date,
          type: 'datetime',
        },
      ];
    },
    displayDate(date) {
      const nDate = new Date(date);
      // eslint-disable-next-line no-restricted-globals
      if (nDate instanceof Date && !isNaN(nDate)) {
        return nDate.toLocaleDateString(); // only date
      }
      return '---';
    },
  },
};
</script>
<style scoped>
  .title-text, .added-ethics strong {
    font-size: 0.93rem !important; /* larger for readability */
    font-weight: 500;
  }

  .subtitle-text, .added-ethics p {
    font-size: 0.90rem !important;
    color: #616161 !important;
  }
</style>
